/**
 * Credee LiveKit demo — configuration + wire-protocol constants.
 *
 * This page talks to `prism-backend-python/livecall_agent`, which since
 * commit 11b032b2 is mounted INSIDE the root backend app (port 8004) at the
 * `/livecall` prefix — with the app's `root_path="/ai"`, the effective base
 * is `<host>:8004/ai/livecall`. The default base is RELATIVE (`/ai/livecall`)
 * so the page works from any origin that can reach it: `ng serve` proxies
 * `/ai` to `127.0.0.1:8004` (crm/proxy.conf.json — this also makes the demo
 * reachable from other devices via `npm run start:lan`), and the deployed
 * servers publish `/ai` on the same origin via Apache. Point it anywhere else
 * by opening the page once with `?api=https://host/ai/livecall` (persisted to
 * localStorage). The LiveKit server URL is never configured here — it arrives
 * as `ws_url` in the backend's `/token` response (see the room service's
 * resolveWsUrl for how localhost ws_urls are made reachable for guests).
 */

export const DEFAULT_API_BASE = '/ai/livecall';

/**
 * Loads Credee's web fonts once (Plus Jakarta Sans + Mulish — the families
 * every cdlk-* rule asks for FIRST; without this they silently fall back to
 * Segoe UI). Same runtime-injection pattern as credee-demo's injectFonts():
 * one idempotent <link>, no global stylesheet change. Called by both the
 * setup and call pages so guests who deep-link into `/call` render with the
 * same type as the host.
 */
export function ensureBrandFonts(): void {
  const id = 'cdlk-fonts';
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Mulish:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const API_STORAGE_KEY = 'credee-livekit-api-base';

/**
 * Resolve the backend base URL: `?api=` query param (persisted for later
 * visits) → localStorage → {@link DEFAULT_API_BASE}. Trailing slash stripped
 * so callers can always write `${base}/path`.
 */
export function resolveApiBase(): string {
  let base = '';
  try {
    const fromQuery = new URLSearchParams(location.search).get('api');
    if (fromQuery) {
      localStorage.setItem(API_STORAGE_KEY, fromQuery);
      base = fromQuery;
    } else {
      base = localStorage.getItem(API_STORAGE_KEY) ?? '';
    }
  } catch {
    // Storage unavailable (private mode / embedded) — fall through to default.
  }
  return (base || DEFAULT_API_BASE).replace(/\/+$/, '');
}

/**
 * Wire-protocol constants — ground truth is the backend/reference client:
 * `livecall_agent/app/presentation.py` (SLIDE_TOPIC / CURRENT_SLIDE_ATTR) and
 * `livecall_agent/web/app/room/page.tsx` (agent identity prefix).
 */

/** LiveKit data-message topic the agent broadcasts slide changes on. */
export const SLIDE_TOPIC = 'slide';

/** Participant attribute holding the agent's current slide index (string int). */
export const SLIDE_ATTRIBUTE = 'current_slide';

/** The AI presenter's participant identity starts with this prefix. */
export const AGENT_IDENTITY_PREFIX = 'agent';

/**
 * A live human consultant (CRM sales rep) pulled in by the "Connect with
 * Consultant" ring joins under this identity prefix. Must match
 * REP_IDENTITY_PREFIX in `livecall_agent/app/api/agent_ring.py`. Lets the
 * roster label their tile "Consultant" and the host step Alex back when they
 * join.
 */
export const REP_IDENTITY_PREFIX = 'rep-';

/** How long to wait for the agent to join before hinting the worker is down. */
export const AGENT_JOIN_TIMEOUT_MS = 20_000;

/** LiveKit text-stream topic carrying live transcriptions (SDK convention). */
export const TRANSCRIPTION_TOPIC = 'lk.transcription';

/**
 * LiveKit data-message topic the backend pushes guest join requests on
 * (`POST /join-requests` → server-side SendData into the room). Must match
 * `JOIN_REQUEST_TOPIC` in `livecall_agent/app/api/server.py`.
 */
export const JOIN_REQUEST_TOPIC = 'join_request';

/**
 * LiveKit data-message topic the backend broadcasts just before deleting the
 * room when the host ends the demo (`POST /meetings/{room}/end`). Guests flip
 * to the "demo ended" screen on it; the ROOM_DELETED disconnect that follows
 * is the fallback signal. Must match `DEMO_ENDED_TOPIC` in
 * `livecall_agent/app/api/server.py`.
 */
export const DEMO_ENDED_TOPIC = 'demo_ended';

/**
 * LiveKit data-message topic carrying the Pause/Resume-AI intent, `{ paused: boolean }`.
 * Sent by the host OR the consultant so anyone can step Alex back without needing
 * server-side moderation rights, and mirrored by peers so every button stays in sync.
 * Must match `AI_CONTROL_TOPIC` in `livecall_agent/app/agent/pause_control.py`.
 */
export const AI_CONTROL_TOPIC = 'ai_control';

/**
 * Identities minted by the backend's host path (`POST /token`) start with
 * this prefix; guest identities start with `guest-`. Lets guests recognize
 * the host in the roster for the host-gone watchdog.
 */
export const HOST_IDENTITY_PREFIX = 'host-';

/**
 * How long guests wait for a vanished host (closed tab, crashed browser) to
 * come back before treating the demo as ended. Long enough to ride out a
 * host page refresh or reconnect — both land well inside it.
 */
export const HOST_GONE_GRACE_MS = 60_000;

/** How often a waiting guest polls `GET /join-requests/{id}`. */
export const GUEST_POLL_INTERVAL_MS = 2_000;

/**
 * How long a guest waits for the host before giving up. Mirrors the backend's
 * `_JOIN_REQUEST_TTL` (the request expires server-side on the same clock).
 */
export const GUEST_APPROVAL_TIMEOUT_MS = 120_000;

/**
 * localStorage key holding a room's host key (set by the setup page when it
 * creates the meeting). Its presence is what makes the call page the HOST:
 * direct token mint + the admit/deny popup for guest join requests.
 */
export function hostKeyStorageKey(room: string): string {
  return `cdlk-host-${room}`;
}

/**
 * localStorage key holding a room's observer key (set alongside the host key
 * when the meeting is created). The host uses it to build the shareable
 * "observer link" (`/call?room=…&observer=<key>`): a hidden, listen-only join
 * that no other participant — human or agent — ever sees.
 */
export function observerKeyStorageKey(room: string): string {
  return `cdlk-observer-${room}`;
}

/**
 * localStorage key remembering the guest's name across visits — prefills the
 * "Join this demo" gate, same behavior as credee-demo's guest join card.
 */
export const GUEST_NAME_STORAGE_KEY = 'cdlk-guest-name';

/**
 * LiveKit text-stream topic the agent ACCEPTS typed user input on. The
 * worker's AgentSession enables room text input on this topic by default
 * (livekit-agents RoomIO), so `localParticipant.sendText(msg, {topic})`
 * reaches Alex exactly like a spoken turn.
 */
export const CHAT_TOPIC = 'lk.chat';

/**
 * LiveKit data-message topic the HOST relays the research brief on, so
 * guests render the same "Your business" rail (client↔client only — the
 * agent ignores this topic). The host retains the payload and re-publishes
 * it on every ParticipantConnected, which covers late joiners.
 */
export const RESEARCH_TOPIC = 'research_brief';

/**
 * localStorage key holding the profile typed on the setup page (business
 * website + email), keyed per room. Read by the call page in the host's
 * browser to run the pre-call research that fills the "Your business" rail;
 * guests never have it — they receive the brief over {@link RESEARCH_TOPIC}.
 */
export function profileStorageKey(room: string): string {
  return `cdlk-profile-${room}`;
}

/** Shape stored under {@link profileStorageKey}. */
export interface StoredProfile {
  website?: string;
  email?: string;
}

/**
 * Landing-card social proof — REAL, verifiable Credee reviews, copied
 * VERBATIM from credee-demo's pick-layer sync (credee-demo.reviews.ts).
 * Never add quotes by hand; re-copy from that file so every card stays
 * traceable to a real record.
 */
export interface LandingReview {
  quote: string;
  reviewer: string;
  business?: string;
  platform: string;
  rating?: number;
  url: string;
  linkLabel: string;
}

export const LANDING_REVIEWS: LandingReview[] = [
  {
    quote:
      'Jerry Hays provided great customer service and assisted with my transactions and account. Credee and staff have been a positive experience.',
    reviewer: 'Tony Tadeo',
    platform: 'Google',
    rating: 5,
    url: 'https://www.credee.com/our-reviews/',
    linkLabel: 'See our reviews ↗',
  },
  {
    quote:
      'Before, we had clients who loved our services but couldn’t always afford to pay upfront. With the payment plans Credee helped us offer, our customers got what they needed, and it just made things easier for them and for us.',
    reviewer: 'Sayonara Beauty',
    business: 'Sayonara Beauty',
    platform: 'credee.com',
    url: 'https://www.credee.com/our-reviews/',
    linkLabel: 'Read on credee.com ↗',
  },
  {
    quote: 'Alex Smith was very helpful with closing out my contract.',
    reviewer: 'Dwinell Jean-louis',
    platform: 'Trustpilot',
    rating: 5,
    url: 'https://www.trustpilot.com/review/credee.com',
    linkLabel: 'See on Trustpilot ↗',
  },
];

/**
 * "Demo Flow" rail labels per product slug, index-aligned with the deck.
 * The deck itself is the source of truth (the client probes the slide count);
 * products without an entry fall back to plain "Slide N" labels.
 */
export const FLOW_BEATS: Record<string, string[]> = {
  credee: [
    'Welcome',
    'The friction',
    'How it works',
    'Who uses Credee',
    'Plans & pricing',
    'Real proof',
    'Getting set up',
  ],
};
